import { llmChatCompletions } from 'seacloud-sdk';
import type {
  WorldSeed,
  Character,
  NarrativeEvent,
  WorldState,
  WorldStateChanges,
  CausalEvent,
  PlayerChoice,
  PlayerIdentity
} from '../types/game';

interface Message {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export class AIEngine {
  private conversationHistory: Message[] = [];
  private model = 'seaart-mix-sonnet-4-5'; // Use Claude for best narrative quality
  private readonly COMPRESSION_THRESHOLD = 15; // Threshold for triggering compression

  /**
   * Compress conversation history
   * When history messages exceed threshold, compress old messages into summary
   */
  private async compressConversationHistory(): Promise<void> {
    if (this.conversationHistory.length <= this.COMPRESSION_THRESHOLD) {
      return;
    }

    // Keep system messages (world settings) and recent messages
    const systemMessages = this.conversationHistory.filter(m => m.role === 'system');
    const recentMessages = this.conversationHistory.slice(-8); // Keep last 8 messages

    // Messages to compress
    const toCompress = this.conversationHistory.slice(
      systemMessages.length,
      this.conversationHistory.length - 8
    );

    if (toCompress.length === 0) {
      return;
    }

    // Generate compression summary
    const compressPrompt = `Please compress the following game history into a concise summary (within 200 words):

${toCompress.map(m => `${m.role}: ${m.content}`).join('\n')}

Requirements:
1. Keep key events and important decisions
2. Keep character relationship changes
3. Keep world state changes
4. Ignore detailed descriptions, only keep cause-and-effect relationships
5. Return summary text only, no explanation`;

    try {
      const response = await llmChatCompletions({
        model: this.model,
        messages: [{ role: 'user', content: compressPrompt }],
        temperature: 0.3, // Low temperature for stable summary
        max_tokens: 400,
        timeout: 120000 // 2 minute timeout
      });

      const summary = response.choices[0].message.content;

      // Rebuild history: system messages + compressed summary + recent messages
      this.conversationHistory = [
        ...systemMessages,
        {
          role: 'assistant',
          content: `[History Summary] ${summary}`
        },
        ...recentMessages
      ];

      console.log(`Conversation history compressed: ${toCompress.length} messages → 1 summary`);
    } catch (error) {
      console.error('Conversation history compression failed:', error);
      // If compression fails, simply truncate history
      this.conversationHistory = [
        ...systemMessages,
        ...this.conversationHistory.slice(-10)
      ];
    }
  }

  /**
   * Generate world seed
   */
  async generateWorld(worldTheme?: string, worldDescription?: string): Promise<WorldSeed> {
    let prompt = '';

    if (worldDescription) {
      // Player provided custom world description
      prompt = `Generate a Roguelike game world based on the following custom description:

"${worldDescription}"

Return in JSON format:
{
  "type": "dark-fantasy",
  "conflict": "kingdom-collapse",
  "rules": {"hasGods": true, "magicTechLevel": "medium", "hasMoralConsequences": true},
  "name": "Twilight Kingdom",
  "description": "A dark fantasy world where the kingdom is collapsing"
}

Requirements:
1. type must reflect the custom description theme
2. conflict must be one of: kingdom-collapse, god-fallen, apocalypse, civilization-clash
3. description within 150 words, atmospheric, aligned with custom description
4. name and description must be in English
5. Return JSON only, no explanation

JSON:`;
    } else if (worldTheme) {
      // Player selected a theme
      prompt = `Generate a Roguelike game world with the theme: ${worldTheme}

Return in JSON format:
{
  "type": "${worldTheme}",
  "conflict": "kingdom-collapse",
  "rules": {"hasGods": true, "magicTechLevel": "medium", "hasMoralConsequences": true},
  "name": "Twilight Kingdom",
  "description": "A dark fantasy world where the kingdom is collapsing"
}

Requirements:
1. type must be: ${worldTheme}
2. conflict must be one of: kingdom-collapse, god-fallen, apocalypse, civilization-clash
3. description within 150 words, atmospheric, matching ${worldTheme} theme
4. name and description must be in English
5. Return JSON only, no explanation

JSON:`;
    } else {
      // Random generation
      prompt = `Generate a Roguelike game world, return in JSON format:

Example:
{
  "type": "dark-fantasy",
  "conflict": "kingdom-collapse",
  "rules": {"hasGods": true, "magicTechLevel": "medium", "hasMoralConsequences": true},
  "name": "Twilight Kingdom",
  "description": "A dark fantasy world where the kingdom is collapsing"
}

Requirements:
1. type must be one of: dark-fantasy, steampunk, cultivation, cyberpunk, lovecraftian, mixed
2. conflict must be one of: kingdom-collapse, god-fallen, apocalypse, civilization-clash
3. description within 150 words, atmospheric
4. name and description must be in English
5. Return JSON only, no explanation

JSON:`;
    }

    const response = await llmChatCompletions({
      model: this.model,
      messages: [{ role: 'user', content: prompt }],
      temperature: 0.9,
      max_tokens: 600,
      timeout: 120000 // 2分钟超时
    });

    const content = response.choices[0].message.content;

    // 提取并清理 JSON
    const jsonStr = this.extractJSON(content, 'object');
    if (!jsonStr) {
      throw new Error('AI 未返回有效的世界数据');
    }

    const cleanedJSON = this.cleanJSON(jsonStr);
    let world: WorldSeed;

    try {
      world = JSON.parse(cleanedJSON);
    } catch (parseError) {
      console.error('World JSON parsing error:', parseError);
      console.error('Cleaned content:', cleanedJSON);
      throw new Error('AI returned world data in unparseable format');
    }

    // Save system settings to conversation history
    this.conversationHistory.push({
      role: 'system',
      content: `You are the Game Master (GM) of "Fate Weaver". This is a Roguelike × AI dynamic narrative game.

[Current World Settings]
${JSON.stringify(world, null, 2)}

[Your Core Responsibilities]
1. Generate dynamic plot events based on world settings
2. Create real cause-and-effect consequences for each player choice
3. Track character fates and world state changes
4. Advance the main conflict at appropriate times
5. Make players feel that "choices really matter"

[Narrative Style]
- Full of atmosphere and tension
- Avoid clichés, pursue uniqueness
- Characters have real motivations and secrets
- The world reacts to player behavior
- All content must be in English

[Output Format]
Always use JSON format, ensure clear parseable structure.
All text content (descriptions, names, dialogue, etc.) must be in English.`
    });

    return world;
  }

  /**
   * Generate player character identity
   */
  async generatePlayerIdentity(
    world: WorldSeed,
    characterName?: string,
    characterTraits?: string
  ): Promise<PlayerIdentity> {
    let nameInstruction = '';
    let traitsInstruction = '';

    if (characterName) {
      nameInstruction = `\nIMPORTANT: The player's character name is "${characterName}". Use this name in the background and identity.`;
    }

    if (characterTraits) {
      traitsInstruction = `\nIMPORTANT: The player's character traits are: "${characterTraits}". Incorporate these traits into the background, coreTrait, and buildPath selection.`;
    }

    const prompt = `Based on world settings, generate player identity in JSON format:

Example:
{
  "background": "Your origin background description",
  "secretPast": "Secret from the past",
  "coreTrait": "knowledge",
  "buildPath": "knowledge",
  "socialClass": "Fallen Scholar",
  "inventory": ["Item1", "Item2", "Item3"],
  "skills": ["Skill1", "Skill2"]
}

World: ${world.description.substring(0, 100)}
Conflict: ${world.conflict}${nameInstruction}${traitsInstruction}

Requirements:
1. coreTrait must be one of: mercy, ambition, fear-gods, order, chaos, knowledge
2. buildPath must be one of: combat, manipulation, faith, knowledge
3. background and secretPast related to world settings, concise (within 50 words each)
4. socialClass brief (e.g., Commoner/Noble/Scholar)
5. inventory has 3 brief item names (within 10 words each)
6. skills has 2 brief skill names (within 10 words each)
7. All text content must be in English
8. Return pure JSON only, no markdown code blocks, no explanation

JSON:`;

    const response = await llmChatCompletions({
      model: this.model,
      messages: [...this.conversationHistory, { role: 'user', content: prompt }],
      temperature: 0.85,
      max_tokens: 800,
      timeout: 120000 // 2 minute timeout
    });

    const content = response.choices[0].message.content;

    // Use strong extraction and cleaning
    const jsonStr = this.extractJSON(content, 'object');
    if (!jsonStr) {
      console.error('Player identity extraction failed, original content:', content);
      throw new Error('AI did not return valid player identity data');
    }

    const cleanedJSON = this.cleanJSON(jsonStr);

    try {
      const identity: PlayerIdentity = JSON.parse(cleanedJSON);
      console.log('[AIEngine] ✓ Player identity generation successful');
      return identity;
    } catch (parseError) {
      console.error('Player identity JSON parsing error:', parseError);
      console.error('Cleaned content:', cleanedJSON);

      // Show problem location characters
      const match = parseError instanceof SyntaxError && parseError.message.match(/position (\d+)/);
      if (match) {
        const pos = parseInt(match[1]);
        const start = Math.max(0, pos - 20);
        const end = Math.min(cleanedJSON.length, pos + 20);
        const snippet = cleanedJSON.substring(start, end);
        const problemChar = cleanedJSON.charAt(pos);
        console.error(`Problem at position ${pos}:`, snippet);
        console.error(`Problem character: "${problemChar}" (charCode: ${problemChar.charCodeAt(0)})`);
      }

      throw new Error('AI returned player identity data in unparseable format');
    }
  }

  /**
   * 强力清理 JSON 字符串
   */
  private cleanJSON(str: string): string {
    let cleaned = str
      // 移除所有换行和回车
      .replace(/[\r\n]+/g, ' ')
      // 移除多余空格
      .replace(/\s+/g, ' ')
      // 修复中文引号为英文引号
      .replace(/[""]/g, '"')
      .replace(/['']/g, "'")
      // 修复中文标点为英文标点
      .replace(/，/g, ',')  // 中文逗号
      .replace(/。/g, '.')  // 中文句号
      .replace(/：/g, ':')  // 中文冒号
      .replace(/；/g, ';')  // 中文分号
      .replace(/！/g, '!')  // 中文感叹号
      .replace(/？/g, '?')  // 中文问号
      .replace(/（/g, '(')  // 中文左括号
      .replace(/）/g, ')')  // 中文右括号
      .replace(/【/g, '[')  // 中文左方括号
      .replace(/】/g, ']')  // 中文右方括号
      .replace(/《/g, '<')  // 中文左书名号
      .replace(/》/g, '>')  // 中文右书名号
      .trim();

    // 智能修复字符串值内的引号
    // 策略: 逐字符解析,跟踪是否在字符串内
    let result = '';
    let inString = false;
    let inKey = false; // 是否在键名中
    let afterColon = false; // 是否刚过冒号

    for (let i = 0; i < cleaned.length; i++) {
      const char = cleaned[i];
      const prevChar = i > 0 ? cleaned[i - 1] : '';
      const nextChar = i < cleaned.length - 1 ? cleaned[i + 1] : '';

      if (char === '"') {
        // 检查是否是转义的引号
        if (prevChar === '\\') {
          result += char;
          continue;
        }

        if (!inString) {
          // 开始一个字符串
          inString = true;
          inKey = !afterColon; // 如果不在冒号后,说明是键名
          result += char;
        } else {
          // 可能结束字符串,检查下一个字符
          // 如果是 :, {, }, [, ], 或空白,说明字符串真的结束了
          if (nextChar === ':' || nextChar === ',' || nextChar === '}' || nextChar === ']' || /\s/.test(nextChar)) {
            // 真正的字符串结束
            inString = false;
            if (nextChar === ':') {
              afterColon = true;
            } else {
              afterColon = false;
            }
            result += char;
          } else {
            // 字符串内的引号,需要转义
            result += '\\"';
          }
        }
      } else {
        result += char;
        if (char === ':' && !inString) {
          afterColon = true;
        } else if ((char === ',' || char === '{' || char === '[') && !inString) {
          afterColon = false;
        }
      }
    }

    cleaned = result
      // 移除尾随逗号
      .replace(/,\s*([}\]])/g, '$1')
      // 移除对象/数组前的逗号
      .replace(/([{\[])\s*,/g, '$1')
      // 移除连续逗号
      .replace(/,\s*,/g, ',')
      // 移除注释
      .replace(/\/\*[\s\S]*?\*\//g, '')
      .replace(/\/\/.*/g, '');

    // 再次检查并清理任何非ASCII控制字符
    cleaned = cleaned.replace(/[\x00-\x1F\x7F-\x9F]/g, '');

    return cleaned;
  }

  /**
   * 从文本中提取 JSON
   */
  private extractJSON(text: string, type: 'object' | 'array'): string | null {
    // 尝试1: 从代码块中提取（优先）
    const codeBlockPattern = type === 'object'
      ? /```(?:json)?\s*(\{[\s\S]*?\})\s*```/
      : /```(?:json)?\s*(\[[\s\S]*?\])\s*```/;

    let match = text.match(codeBlockPattern);
    if (match) return match[1];

    // 尝试2: 直接匹配
    const pattern = type === 'object' ? /\{[\s\S]*\}/ : /\[[\s\S]*\]/;
    match = text.match(pattern);
    if (match) return match[0];

    // 尝试3: 查找第一个 { 或 [ 到最后一个 } 或 ]
    const start = type === 'object' ? text.indexOf('{') : text.indexOf('[');
    const end = type === 'object' ? text.lastIndexOf('}') : text.lastIndexOf(']');

    if (start !== -1 && end !== -1 && end > start) {
      return text.substring(start, end + 1);
    }

    return null;
  }

  /**
   * Generate initial characters (optimized version, ensure success)
   */
  async generateInitialCharacters(world: WorldSeed): Promise<Character[]> {
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`[AIEngine] Attempting to generate characters (${attempt}/${maxRetries})...`);

        const prompt = `Generate 3 game characters, must return valid JSON array.

Example format:
[
  {
    "id": "char_1",
    "name": "Aria",
    "description": "Mysterious oracle",
    "alive": true,
    "relationship": 0,
    "role": "Prophet",
    "secrets": ["Knows the world's secrets"],
    "fate": "Will reveal the truth"
  }
]

Based on this world, generate 3 characters:
- World: ${world.description.substring(0, 100)}
- Conflict: ${world.conflict}

Requirements:
1. Return valid JSON array with 3 characters
2. Each character must have id, name, description, role, secrets, fate
3. description keep brief (within 50 words)
4. secrets array contains 1-2 secrets
5. All text content (names, descriptions, etc.) must be in English
6. Do not add any explanation, return JSON only
7. IMPORTANT: Avoid using English double quotes (") in text content, use single quotes, dashes, or omit

JSON array:`;

        const response = await llmChatCompletions({
          model: this.model,
          messages: [...this.conversationHistory, { role: 'user', content: prompt }],
          temperature: 0.7,
          max_tokens: 2000,  // 增加 token 限制,确保能生成完整的 3 个角色
          timeout: 120000 // 2分钟超时
        });

        const content = response.choices[0].message.content;
        console.log('[AIEngine] Original response length:', content.length);
        console.log('[AIEngine] Original response:', content);

        // Extract JSON
        const jsonStr = this.extractJSON(content, 'array');
        if (!jsonStr) {
          throw new Error('JSON array not found');
        }

        console.log('[AIEngine] Extracted JSON length:', jsonStr.length);
        console.log('[AIEngine] Extracted raw JSON:', jsonStr);

        // Clean JSON
        const cleanedJSON = this.cleanJSON(jsonStr);
        console.log('[AIEngine] Cleaned JSON length:', cleanedJSON.length);

        // Parse
        let baseCharacters: Character[];
        try {
          baseCharacters = JSON.parse(cleanedJSON);
        } catch (parseError) {
          console.error('[AIEngine] JSON parsing failed:', parseError);
          console.error('[AIEngine] Full cleaned JSON:', cleanedJSON);

          // Show problem location
          const match = parseError instanceof SyntaxError && parseError.message.match(/position (\d+)/);
          if (match) {
            const pos = parseInt(match[1]);
            const start = Math.max(0, pos - 30);
            const end = Math.min(cleanedJSON.length, pos + 30);
            const snippet = cleanedJSON.substring(start, end);
            const problemChar = cleanedJSON.charAt(pos);
            console.error(`[AIEngine] Problem at position ${pos}:`, snippet);
            console.error(`[AIEngine] Problem character: "${problemChar}" (charCode: ${problemChar.charCodeAt(0)})`);
          }
          throw parseError;
        }

        // Validate
        if (!Array.isArray(baseCharacters) || baseCharacters.length === 0) {
          throw new Error('Parsed result is not a valid array');
        }

        // Validate required fields for each character
        for (const char of baseCharacters) {
          if (!char.id || !char.name || !char.role) {
            throw new Error(`Character ${char.name || 'Unknown'} missing required fields`);
          }
          // Fill missing fields
          char.description = char.description || 'Mysterious character';
          char.alive = char.alive !== false;
          char.relationship = char.relationship || 0;
          char.secrets = char.secrets || ['Unknown secret'];
          char.fate = char.fate || 'Fate unknown';
        }

        console.log(`[AIEngine] ✓ Successfully generated ${baseCharacters.length} characters`);

        // 添加隐藏属性
        return baseCharacters.map(char => ({
          ...char,
          betrayalPotential: this.estimateBetrayalPotential(char.description, char.role),
          moralBottom: '未知',
          stance: '观望中',
          desire: '生存',
          fear: '死亡',
          memoryOfPlayer: [],
          plannedActions: []
        }));

      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        console.error(`[AIEngine] Attempt ${attempt} failed:`, lastError.message);

        if (attempt < maxRetries) {
          console.log(`[AIEngine] Waiting 1 second before retry...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }

    // All retries failed
    throw new Error(`Character generation failed (retried ${maxRetries} times): ${lastError?.message}`);
  }

  /**
   * 根据角色描述估算背叛倾向
   */
  private estimateBetrayalPotential(description: string, role: string): number {
    const lowKeywords = ['忠诚', '骑士', '守护', '信仰', '正直'];
    const highKeywords = ['复仇', '野心', '背叛', '刺客', '阴谋', '堕落'];

    const lower = description.toLowerCase() + role.toLowerCase();

    let score = 50; // 默认中等
    lowKeywords.forEach(kw => {
      if (lower.includes(kw)) score -= 15;
    });
    highKeywords.forEach(kw => {
      if (lower.includes(kw)) score += 15;
    });

    return Math.max(0, Math.min(100, score));
  }

  /**
   * Generate opening event
   */
  async generateOpeningEvent(
    _world: WorldSeed,
    characters: Character[]
  ): Promise<NarrativeEvent> {
    const characterSummary = characters.map(c =>
      `${c.name}(${c.role})`
    ).join(', ');

    const prompt = `Generate opening event, return in JSON format:

Example:
{
  "id": "event_001",
  "title": "Event Title",
  "description": "Event description",
  "choices": [
    {"id": "choice_1", "text": "Option 1", "consequences": "Consequence hint"}
  ],
  "characters": ["char_1"],
  "location": "Location"
}

Characters: ${characterSummary}

Requirements:
1. Immediately involve the player in the core conflict
2. Provide 3-4 options, each leading to different consequences
3. description keep concise (within 100 words)
4. consequences keep brief (within 30 words)
5. Involve 1-2 characters
6. All text content (title, description, choices, location) must be in English
7. Return JSON only, no explanation
8. IMPORTANT: Avoid using English double quotes (") in text content, use single quotes, dashes, or omit

JSON:`;

    const response = await llmChatCompletions({
      model: this.model,
      messages: [...this.conversationHistory, { role: 'user', content: prompt }],
      temperature: 0.9,
      max_tokens: 1500,  // Increase token limit to accommodate full event
      timeout: 120000 // 2 minute timeout
    });

    const content = response.choices[0].message.content;

    // Use strong extraction and cleaning
    const jsonStr = this.extractJSON(content, 'object');
    if (!jsonStr) {
      throw new Error('AI did not return valid event data');
    }

    const cleanedJSON = this.cleanJSON(jsonStr);

    let event: NarrativeEvent;
    try {
      event = JSON.parse(cleanedJSON);
    } catch (parseError) {
      console.error('Event JSON parsing error:', parseError);
      console.error('Cleaned content:', cleanedJSON);
      throw new Error('AI returned event data in unparseable format');
    }

    event.timestamp = Date.now();
    console.log('[AIEngine] ✓ Opening event generation successful');

    return event;
  }

  /**
   * Process player choice, generate subsequent events
   */
  async processChoice(
    choice: PlayerChoice,
    currentEvent: NarrativeEvent,
    worldState: WorldState,
    characters: Character[]
  ): Promise<{
    causalEvent: CausalEvent;
    nextEvent: NarrativeEvent;
    updatedCharacters: Character[];
    worldStateChanges: WorldStateChanges;
  }> {
    // Check if history compression is needed before processing choice
    await this.compressConversationHistory();

    const characterSummary = characters.map(c =>
      `${c.name} (${c.alive ? 'Alive' : 'Dead'}, Relationship: ${c.relationship})`
    ).join('\n');

    const prompt = `Player chose in "${currentEvent.title}": "${choice.text}"

[Current World State]
Tension: ${worldState.tension}/100
Turn: ${worldState.turnCount}
Unresolved Conflicts: ${worldState.unresolvedConflicts.join(', ')}

[Current Character Status]
${characterSummary}

Please generate:
1. Causal event (direct consequences of player action)
2. Next narrative event
3. Character status changes
4. World state changes

Return in JSON format:
{
  "causalEvent": {
    "id": "causal_xxx",
    "description": "Detailed description of consequences from this choice",
    "trigger": "${choice.text}",
    "consequences": ["Consequence 1", "Consequence 2"],
    "affectedCharacters": ["Character ID"],
    "worldImpact": number from -5 to 5
  },
  "nextEvent": {
    "id": "event_xxx",
    "title": "Title",
    "description": "Description",
    "choices": [...],
    "characters": [...],
    "location": "Location"
  },
  "characterChanges": [
    {
      "id": "Character ID",
      "alive": true/false,
      "relationship": change value (-20 to 20),
      "newSecrets": ["if there are new secrets"]
    }
  ],
  "worldStateChanges": {
    "tensionChange": -10 to 10,
    "newConflicts": ["if there are new conflicts"],
    "resolvedConflicts": ["if conflicts are resolved"],
    "majorEvent": "if a major event occurs"
  }
}

Requirements:
1. Consequences must be logically related to player choice
2. World must react to player behavior
3. Character relationships must change realistically
4. Maintain narrative tension, do not end story too early
5. All text content must be in English
6. Return JSON only`;

    console.log('[AIEngine] processChoice starting, choice:', choice.text);

    const response = await llmChatCompletions({
      model: this.model,
      messages: [...this.conversationHistory, { role: 'user', content: prompt }],
      temperature: 0.9,
      max_tokens: 2500,  // Increase to 2500 to ensure complete generation
      timeout: 120000 // 2 minute timeout
    });

    console.log('[AIEngine] processChoice API response successful');

    const content = response.choices[0].message.content;
    console.log('[AIEngine] Original response length:', content.length);

    // Use extractJSON to extract JSON
    const jsonStr = this.extractJSON(content, 'object');
    if (!jsonStr) {
      console.error('[AIEngine] Cannot extract JSON, original content:', content);
      throw new Error('AI did not return valid result data');
    }

    // Use cleanJSON to clean JSON
    const cleanedJson = this.cleanJSON(jsonStr);
    console.log('[AIEngine] Cleaned JSON length:', cleanedJson.length);

    // Try to fix possibly truncated JSON
    let fixedJson = cleanedJson;

    // Check if JSON is complete
    const openBraces = (fixedJson.match(/\{/g) || []).length;
    const closeBraces = (fixedJson.match(/\}/g) || []).length;
    const openBrackets = (fixedJson.match(/\[/g) || []).length;
    const closeBrackets = (fixedJson.match(/\]/g) || []).length;

    // If unbalanced, try to auto-complete
    if (openBraces > closeBraces || openBrackets > closeBrackets) {
      console.warn('[AIEngine] JSON may be truncated, attempting to fix...');

      // Remove possibly incomplete last element
      // Find last complete array element or object
      const lastComma = fixedJson.lastIndexOf(',');
      if (lastComma > 0) {
        fixedJson = fixedJson.substring(0, lastComma);
      }

      // Fill missing brackets
      for (let i = 0; i < openBrackets - closeBrackets; i++) {
        fixedJson += ']';
      }
      for (let i = 0; i < openBraces - closeBraces; i++) {
        fixedJson += '}';
      }

      console.log('[AIEngine] Fixed JSON length:', fixedJson.length);
    }

    let result;
    try {
      result = JSON.parse(fixedJson);
      console.log('[AIEngine] JSON parsing successful');
    } catch (parseError) {
      console.error('[AIEngine] JSON parsing error:', parseError);
      console.error('[AIEngine] Fixed JSON:', fixedJson);
      console.error('[AIEngine] Original content:', content);
      throw new Error('AI returned result data in unparseable format');
    }

    // Update character state
    const updatedCharacters = characters.map(char => {
      const change = result.characterChanges?.find((c: any) => c.id === char.id);
      if (!change) return char;

      return {
        ...char,
        alive: change.alive !== undefined ? change.alive : char.alive,
        relationship: Math.max(-100, Math.min(100, char.relationship + (change.relationship || 0))),
        secrets: change.newSecrets ? [...(char.secrets || []), ...change.newSecrets] : char.secrets
      };
    });

    // Add timestamp
    result.nextEvent.timestamp = Date.now();

    // Save this interaction to history
    this.conversationHistory.push(
      { role: 'user', content: `Player choice: ${choice.text}` },
      { role: 'assistant', content: JSON.stringify(result.causalEvent) }
    );

    return {
      causalEvent: result.causalEvent,
      nextEvent: result.nextEvent,
      updatedCharacters,
      worldStateChanges: result.worldStateChanges
    };
  }

  /**
   * 检查并生成 NPC 主动行动事件
   * 每 2-3 回合触发一次，让 NPC 基于其记忆和计划主动推进剧情
   */
  async checkNPCProactiveActions(
    characters: Character[],
    worldState: WorldState,
    playerHistory: string[]
  ): Promise<NarrativeEvent | null> {
    // 只在特定回合触发（第3、6、9、12回合）
    const triggerTurns = [3, 6, 9, 12];
    if (!triggerTurns.includes(worldState.turnCount)) {
      return null;
    }

    // 收集有潜在行动的 NPC
    const activeNPCs = characters.filter(c =>
      c.alive &&
      (c.memoryOfPlayer && c.memoryOfPlayer.length > 0 ||
       c.plannedActions && c.plannedActions.length > 0 ||
       c.betrayalPotential && c.betrayalPotential > 60)
    );

    if (activeNPCs.length === 0) {
      return null;
    }

    // 构建 NPC 状态摘要
    const npcSummary = activeNPCs.map(c =>
      `${c.name}（${c.role}）:
       - 关系值: ${c.relationship}
       - 背叛倾向: ${c.betrayalPotential || 0}
       - 立场: ${c.stance || '未知'}
       - 欲望: ${c.desire || '未知'}
       - 恐惧: ${c.fear || '未知'}
       - 记住的玩家行为: ${c.memoryOfPlayer?.join('、') || '无'}
       - 策划的行动: ${c.plannedActions?.join('、') || '无'}`
    ).join('\n\n');

    const prompt = `【NPC 主动行动生成】

当前是第 ${worldState.turnCount} 回合。某个 NPC 决定主动采取行动，而不是被动等待玩家选择。

【活跃的 NPC 状态】
${npcSummary}

【玩家历史行为】
${playerHistory.slice(-5).join(' → ')}

【世界状态】
紧张度: ${worldState.tension}/100
未解决冲突: ${worldState.unresolvedConflicts.join('、')}

请选择一个最有动机主动行动的 NPC，基于其记忆、欲望、恐惧或策划，生成一个 NPC 主导的事件（以 JSON 格式回复）:

{
  "id": "npc_action_xxx",
  "title": "事件标题（体现 NPC 的主动性）",
  "description": "200字以内的描述。重点：这是 NPC 主动策划并执行的行动，玩家被动卷入。要体现因果关系（例如：'你在第一章放过的刺客，现在带着军队回来了'）",
  "initiatingNPC": "发起行动的 NPC ID",
  "npcMotivation": "NPC 为何在此刻采取行动（基于其记忆、欲望、恐惧）",
  "choices": [
    {
      "id": "choice_1",
      "text": "玩家的应对选项",
      "consequences": "可能后果"
    }
  ],
  "characters": ["涉及的角色ID"],
  "location": "地点",
  "riskLevel": "low | medium | high | extreme"
}

要求：
1. NPC 的行动必须与其之前的记忆或策划逻辑相关
2. 体现"蝴蝶效应"：玩家早期的某个小选择现在产生了重大后果
3. NPC 要有明确的动机（复仇、野心、恐惧等）
4. 制造戏剧冲突：让玩家感受到"世界在主动反应"
5. 只返回 JSON，不要解释`;

    const response = await llmChatCompletions({
      model: this.model,
      messages: [...this.conversationHistory, { role: 'user', content: prompt }],
      temperature: 0.9,
      max_tokens: 1200,
      timeout: 120000 // 2分钟超时
    });

    const content = response.choices[0].message.content;
    let jsonMatch = content.match(/\{[\s\S]*\}/);

    if (!jsonMatch) {
      console.warn('NPC 主动行动生成失败，跳过本回合');
      return null;
    }

    let jsonStr = jsonMatch[0]
      .replace(/,(\s*[}\]])/g, '$1')
      .replace(/\n/g, ' ')
      .replace(/\r/g, '')
      .replace(/\t/g, ' ');

    try {
      const npcEvent = JSON.parse(jsonStr);
      npcEvent.timestamp = Date.now();
      npcEvent.isNPCInitiated = true; // 标记为 NPC 主导事件

      // 记录到对话历史
      this.conversationHistory.push({
        role: 'assistant',
        content: `NPC 主动事件: ${npcEvent.title}`
      });

      return npcEvent;
    } catch (parseError) {
      console.error('NPC 行动 JSON 解析错误:', parseError);
      console.error('原始内容:', content);
      return null;
    }
  }

  /**
   * 更新 NPC 的记忆和策划
   * 在玩家做出选择后，更新相关 NPC 的 memoryOfPlayer 和 plannedActions
   */
  async updateNPCMemoryAndPlans(
    affectedCharacters: string[],
    playerChoice: string,
    causalEvent: CausalEvent,
    allCharacters: Character[]
  ): Promise<Character[]> {
    const updatedCharacters = [...allCharacters];

    for (const charId of affectedCharacters) {
      const char = updatedCharacters.find(c => c.id === charId);
      if (!char || !char.alive) continue;

      // 将玩家行为添加到 NPC 记忆
      if (!char.memoryOfPlayer) {
        char.memoryOfPlayer = [];
      }
      char.memoryOfPlayer.push(`${playerChoice} (影响: ${causalEvent.description})`);

      // 保留最近 5 条记忆（防止 Token 过多）
      if (char.memoryOfPlayer.length > 5) {
        char.memoryOfPlayer = char.memoryOfPlayer.slice(-5);
      }

      // 基于关系值和记忆，生成 NPC 的下一步策划
      if (Math.abs(char.relationship) > 50 || char.betrayalPotential && char.betrayalPotential > 60) {
        const prompt = `${char.name} 是一个 ${char.role}。

【角色属性】
关系值: ${char.relationship}
背叛倾向: ${char.betrayalPotential || 0}
立场: ${char.stance || '未知'}
欲望: ${char.desire || '未知'}
恐惧: ${char.fear || '未知'}

【记住的玩家行为】
${char.memoryOfPlayer.join('\n')}

基于以上信息，这个 NPC 会策划什么行动？返回 JSON:
{
  "plannedActions": ["行动1（20字以内）", "行动2"]
}

要求：
1. 行动必须符合角色动机
2. 如果关系值极低或背叛倾向高，策划背叛或复仇
3. 如果关系值高，策划帮助或支持
4. 最多 2 个策划
5. 只返回 JSON`;

        try {
          const response = await llmChatCompletions({
            model: this.model,
            messages: [{ role: 'user', content: prompt }],
            temperature: 0.8,
            max_tokens: 300,
            timeout: 120000 // 2分钟超时
          });

          const content = response.choices[0].message.content;
          let jsonMatch = content.match(/\{[\s\S]*\}/);

          if (jsonMatch) {
            let jsonStr = jsonMatch[0]
              .replace(/,(\s*[}\]])/g, '$1')
              .replace(/\n/g, ' ')
              .replace(/\r/g, '')
              .replace(/\t/g, ' ');

            const result = JSON.parse(jsonStr);
            char.plannedActions = result.plannedActions;
          }
        } catch (error) {
          console.warn(`NPC ${char.name} 策划生成失败:`, error);
        }
      }
    }

    return updatedCharacters;
  }

  /**
   * 获取对话历史的 Token 估算
   * 粗略估算: 1 个中文字符 ≈ 2 tokens, 1 个英文单词 ≈ 1.3 tokens
   */
  getEstimatedTokens(): number {
    const totalChars = this.conversationHistory
      .map(m => m.content.length)
      .reduce((sum, len) => sum + len, 0);

    // 粗略估算: 平均每个字符约 1.5 tokens
    return Math.ceil(totalChars * 1.5);
  }

  /**
   * 获取对话历史统计
   */
  getHistoryStats(): {
    messageCount: number;
    estimatedTokens: number;
    compressionNeeded: boolean;
  } {
    return {
      messageCount: this.conversationHistory.length,
      estimatedTokens: this.getEstimatedTokens(),
      compressionNeeded: this.conversationHistory.length > this.COMPRESSION_THRESHOLD
    };
  }

  /**
   * Check if ending should be triggered
   */
  async checkForEnding(worldState: WorldState, characters: Character[]): Promise<{
    shouldEnd: boolean;
    endingType?: 'victory' | 'defeat' | 'neutral' | 'transcendent';
    endingNarrative?: string;
  }> {
    // Simple rule judgment
    if (worldState.turnCount >= 15) {
      // Must end after 15 turns
      const aliveCount = characters.filter(c => c.alive).length;
      const avgRelationship = characters.reduce((sum, c) => sum + c.relationship, 0) / characters.length;

      const endingType =
        worldState.tension < 30 && avgRelationship > 50 ? 'victory' :
        aliveCount <= 1 || worldState.tension > 80 ? 'defeat' :
        avgRelationship > 30 ? 'neutral' : 'defeat';

      const prompt = `Generate game ending narrative (within 200 words):

Ending Type: ${endingType}
World Tension: ${worldState.tension}
Alive Characters: ${aliveCount}/${characters.length}
Average Relationship: ${avgRelationship.toFixed(0)}

Requirements:
1. Summarize how player's choices shaped this ending
2. Echo the world's core conflict
3. Give players a feeling of "wanting more", hint at other possibilities
4. All text must be in English
5. Return pure narrative text only, not JSON`;

      const response = await llmChatCompletions({
        model: this.model,
        messages: [...this.conversationHistory, { role: 'user', content: prompt }],
        temperature: 1.0,
        max_tokens: 500,
        timeout: 120000 // 2 minute timeout
      });

      return {
        shouldEnd: true,
        endingType,
        endingNarrative: response.choices[0].message.content
      };
    }

    return { shouldEnd: false };
  }
}
